import React, { useState, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, Users, Calculator, AlertTriangle, Eye, EyeOff } from "lucide-react";

// Type definitions
interface Rule {
  id?: string;
  condition: string;
  action: string;
  count?: number;
  description?: string;
}

interface ScoringRules {
  minJudges?: number;
  maxJudges?: number;
  targetTotalScore?: number;
  maxPointsPerJudge?: number;
  dropLowestScore?: boolean;
  dropScoreConditions?: Rule[];
  tieBreakingRules?: Rule[];
  missingJudgeRules?: Rule[];
  [key: string]: any;
}

interface ScoringRulesInfoProps {
  scoringRules?: ScoringRules | null;
  className?: string;
  compact?: boolean;
}

interface ScoringRulesBannerProps {
  scoringRules?: ScoringRules | null;
  className?: string;
}

/**
 * Component to display scoring rules information to judges
 */
export const ScoringRulesInfo = memo(function ScoringRulesInfo({ 
  scoringRules, 
  className = "",
  compact = false 
}: ScoringRulesInfoProps) {
  const [showDetails, setShowDetails] = useState<boolean>(false);

  if (!scoringRules || Object.keys(scoringRules).length === 0) {
    return null;
  }

  const {
    minJudges = 3,
    maxJudges = 4,
    targetTotalScore = 300,
    maxPointsPerJudge = 100,
    dropLowestScore = false,
    dropScoreConditions = [],
    tieBreakingRules = [],
    missingJudgeRules = []
  } = scoringRules;

  const hasSpecialRules = dropScoreConditions.length > 0 || 
                         tieBreakingRules.length > 0 || 
                         missingJudgeRules.length > 0;

  if (compact) {
    return (
      <div className={`flex items-center space-x-2 text-sm ${className}`}>
        <Users className="w-4 h-4 text-blue-400" />
        <span className="text-gray-300">
          {minJudges === maxJudges ? `${minJudges} judges` : `${minJudges}-${maxJudges} judges`}
        </span>
        <span className="text-gray-500">•</span>
        <span className="text-gray-300">{targetTotalScore} pts total</span>
        {hasSpecialRules && (
          <>
            <span className="text-gray-500">•</span>
            <div className="flex items-center space-x-1">
              <AlertTriangle className="w-3 h-3 text-yellow-400" />
              <span className="text-yellow-300">Special rules</span>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-gray-800/30 rounded-lg border border-gray-700/50 ${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <Calculator className="w-5 h-5 mr-2 text-blue-400" />
            Scoring Rules
          </h3>
          {hasSpecialRules && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center space-x-2 px-3 py-1 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors text-sm"
            >
              {showDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>Details</span>
            </button>
          )}
        </div>

        {/* Basic Rules */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-900/30 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Users className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-gray-300">Judges</span>
            </div>
            <div className="text-lg font-semibold text-white">
              {minJudges === maxJudges ? minJudges : `${minJudges}-${maxJudges}`}
            </div>
          </div>

          <div className="bg-gray-900/30 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Calculator className="w-4 h-4 text-green-400" />
              <span className="text-sm font-medium text-gray-300">Target Score</span>
            </div>
            <div className="text-lg font-semibold text-white">
              {targetTotalScore} pts
            </div>
          </div>
        </div>

        {/* Quick Rules Summary */}
        {hasSpecialRules && (
          <div className="bg-yellow-500/10 border border-yellow-400/30 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-yellow-300 mb-1">Special Rules Active</div>
                <div className="text-yellow-200/80 space-y-1">
                  {dropScoreConditions.length > 0 && (
                    <div>• Score dropping enabled</div>
                  )}
                  {tieBreakingRules.length > 0 && (
                    <div>• Tie-breaking rules configured</div>
                  )}
                  {missingJudgeRules.length > 0 && (
                    <div>• Missing judge handling enabled</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Rules */}
        <AnimatePresence>
          {showDetails && hasSpecialRules && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4"
            >
              {/* Score Dropping Rules */}
              {dropScoreConditions.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Score Dropping</h4>
                  <div className="space-y-2">
                    {dropScoreConditions.map((rule, index) => (
                      <div key={rule.id || index} className="bg-gray-900/30 rounded p-3">
                        <div className="text-sm text-gray-300 mb-1">
                          <strong>When:</strong> {rule.condition}
                        </div>
                        <div className="text-sm text-gray-300 mb-1">
                          <strong>Action:</strong> {rule.action} {rule.count && `(${rule.count})`}
                        </div>
                        {rule.description && (
                          <div className="text-xs text-gray-400">{rule.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tie Breaking Rules */}
              {tieBreakingRules.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Tie Breaking</h4>
                  <div className="space-y-2">
                    {tieBreakingRules.map((rule, index) => (
                      <div key={rule.id || index} className="bg-gray-900/30 rounded p-3">
                        <div className="text-sm text-gray-300 mb-1">
                          <strong>When:</strong> {rule.condition}
                        </div>
                        <div className="text-sm text-gray-300 mb-1">
                          <strong>Action:</strong> {rule.action}
                        </div>
                        {rule.description && (
                          <div className="text-xs text-gray-400">{rule.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Judge Rules */}
              {missingJudgeRules.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Missing Judge Handling</h4>
                  <div className="space-y-2">
                    {missingJudgeRules.map((rule, index) => (
                      <div key={rule.id || index} className="bg-gray-900/30 rounded p-3">
                        <div className="text-sm text-gray-300 mb-1">
                          <strong>When:</strong> {rule.condition}
                        </div>
                        <div className="text-sm text-gray-300 mb-1">
                          <strong>Action:</strong> {rule.action}
                        </div>
                        {rule.description && (
                          <div className="text-xs text-gray-400">{rule.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

/**
 * Compact banner version for judge stations
 */
export const ScoringRulesBanner = memo(function ScoringRulesBanner({ scoringRules, className = "" }: ScoringRulesBannerProps) {
  if (!scoringRules || Object.keys(scoringRules).length === 0) {
    return null;
  }

  const {
    minJudges = 3,
    maxJudges = 4,
    targetTotalScore = 300,
    dropScoreConditions = [],
    tieBreakingRules = [],
    missingJudgeRules = []
  } = scoringRules;

  const hasDropping = dropScoreConditions.length > 0;
  const hasTieBreaking = tieBreakingRules.length > 0;
  const hasMissingJudge = missingJudgeRules.length > 0;

  return (
    <div className={`bg-blue-500/10 border border-blue-400/30 rounded-lg p-3 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Info className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-300">Competition Rules</span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-300">
            <span>{minJudges === maxJudges ? `${minJudges} judges` : `${minJudges}-${maxJudges} judges`}</span>
            <span>•</span>
            <span>{targetTotalScore} pts total</span>
            
            {hasDropping && (
              <>
                <span>•</span>
                <span className="text-orange-300">Score dropping</span>
              </>
            )}
            
            {hasTieBreaking && (
              <>
                <span>•</span>
                <span className="text-yellow-300">Tie breaking</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

